// Daily Verses UI Component
// Fixed part: plan_lecture_365_mg.json (Verset du Jour)
// Random part: weighted selection (75% NT, 60% Psalms in OT)
import { getBooksList, loadBook } from '../bible.js';

const NT_START_INDEX = 39;

// Maps plan JSON livre names (from bible-mg.txt verse refs) to French canonical names
// (used for loadBook() and #post-book compose input)
const MG_TO_FRENCH = {
    'Genesisy': 'Genèse', 'Eksodosy': 'Exode', 'Levitikosy': 'Lévitique',
    'Nomery': 'Nombres', 'Deotoronomia': 'Deutéronome', 'Josoa': 'Josué',
    'Mpitsara': 'Juges', 'Rota': 'Ruth',
    '1 Samoela': '1 Samuel', '2 Samoela': '2 Samuel',
    '1 Mpanjaka': '1 Rois', '2 Mpanjaka': '2 Rois',
    '1 Tantara': '1 Chroniques', '2 Tantara': '2 Chroniques',
    'Ezra': 'Esdras', 'Nehemia': 'Néhémie', 'Estera': 'Esther', 'Joba': 'Job',
    'Salamo': 'Psaumes',
    'Ohabolana': 'Proverbes', 'Mpitoriteny': 'Ecclésiaste',
    "Tonon-kiran'i Solomona": 'Cantique des Cantiques',
    'Isaia': 'Ésaïe', 'Jeremia': 'Jérémie', 'Fitomaniana': 'Lamentations',
    'Ezekiela': 'Ézéchiel', 'Daniela': 'Daniel', 'Hosea': 'Osée',
    'Joela': 'Joël', 'Amosa': 'Amos', 'Obadia': 'Abdias', 'Jona': 'Jonas',
    'Mika': 'Michée', 'Nahoma': 'Nahum', 'Habakoka': 'Habacuc',
    'Zefania': 'Sophonie', 'Hagay': 'Aggée', 'Zakaria': 'Zacharie', 'Malakia': 'Malachie',
    'Matio': 'Matthieu', 'Marka': 'Marc', 'Lioka': 'Luc', 'Jaona': 'Jean',
    "Asan'ny Apostoly": 'Actes', 'Romana': 'Romains',
    '1 Korintiana': '1 Corinthiens', '2 Korintiana': '2 Corinthiens',
    'Galatiana': 'Galates', 'Efesiana': 'Éphésiens', 'Filipiana': 'Philippiens',
    'Kolosiana': 'Colossiens',
    '1 Tesaloniana': '1 Thessaloniciens', '2 Tesaloniana': '2 Thessaloniciens',
    '1 Timoty': '1 Timothée', '2 Timoty': '2 Timothée',
    'Titosy': 'Tite', 'Filemona': 'Philémon', 'Hebreo': 'Hébreux',
    'Jakoba': 'Jacques',
    '1 Petera': '1 Pierre', '2 Petera': '2 Pierre',
    '1 Jaona': '1 Jean', '2 Jaona': '2 Jean', '3 Jaona': '3 Jean',
    'Joda': 'Jude', 'Apokalypsy': 'Apocalypse',
};

let refreshTimer = null;
let versesContainer = null;
let cachedPlan = null;

// ─────────────────────────────────────────
// Public API
// ─────────────────────────────────────────

export async function initDailyVerses(container) {
    versesContainer = container;
    clearRefreshTimer();
    await refreshVerses();
    refreshTimer = setInterval(refreshVerses, 5 * 60 * 1000);
}

export function cleanupDailyVerses() {
    clearRefreshTimer();
    versesContainer = null;
}

// ─────────────────────────────────────────
// Main refresh
// ─────────────────────────────────────────

async function refreshVerses() {
    if (!versesContainer) return;

    versesContainer.innerHTML = `
        <div class="daily-verses-header">
            <div>
                <h2>✨ Versets du moment</h2>
                <p class="daily-verses-subtitle">Verset du jour fixe · Suggestions aléatoires renouvelées toutes les 5 min</p>
            </div>
            <button class="btn btn-secondary" id="refresh-verses-btn">🔄 Renouveler</button>
        </div>
        <div id="daily-verses-list" class="daily-verses-list">
            <div class="loading">⏳ Chargement des versets...</div>
        </div>
    `;

    versesContainer.querySelector('#refresh-verses-btn').addEventListener('click', refreshVerses);
    const list = versesContainer.querySelector('#daily-verses-list');

    try {
        const lang = localStorage.getItem('user_preferred_lang') || 'french';
        const books = await getBooksList('french');
        const [plan] = await Promise.all([loadDailyPlan()]);

        list.innerHTML = '';

        // ── Fixed: Verset du Jour ──────────────────
        if (plan && plan.length > 0) {
            const dayEntry = getTodayEntry(plan);
            const featuredCard = await buildFeaturedCard(dayEntry, lang);
            list.appendChild(featuredCard);
        }

        // ── Random verses (1-5 groups) ─────────────
        const divider = document.createElement('div');
        divider.className = 'daily-verses-divider';
        divider.innerHTML = '<span>Suggestions aléatoires</span>';
        list.appendChild(divider);

        const groupCount = Math.floor(Math.random() * 5) + 1;
        const picks = Array.from({ length: groupCount }, () => pickVerseGroup(books, lang));
        const results = await Promise.allSettled(picks);

        for (const result of results) {
            if (result.status === 'fulfilled' && result.value) {
                list.appendChild(buildVerseCard(result.value));
            }
        }
    } catch (err) {
        console.error('Error loading daily verses:', err);
        list.innerHTML = '<p class="error-state">Erreur lors du chargement. Réessayez.</p>';
    }
}

// ─────────────────────────────────────────
// Daily Plan (fixed)
// ─────────────────────────────────────────

async function loadDailyPlan() {
    if (cachedPlan) return cachedPlan;
    try {
        const resp = await fetch('/data/plan_lecture_365_mg.json');
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        cachedPlan = await resp.json();
        return cachedPlan;
    } catch (e) {
        console.error('Failed to load daily plan:', e);
        return null;
    }
}

function getDayOfYear() {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const diff = now - start;
    return Math.floor(diff / (1000 * 60 * 60 * 24)); // 1-365
}

function getTodayEntry(plan) {
    const day = getDayOfYear();
    return plan[(day - 1 + plan.length) % plan.length];
}

async function buildFeaturedCard(entry, lang) {
    const card = document.createElement('div');
    card.className = 'daily-verse-card daily-verse-card--featured';

    const frenchName = MG_TO_FRENCH[entry.livre] || entry.livre;
    const { verseStart, verseEnd } = getVerseRange(entry.versets);

    // Use pre-built reference from JSON when available, translate livre name to French
    // entry.reference is in Malagasy (e.g. "Salamo 23:1-3"), so we replace the livre part
    let ref;
    if (entry.reference) {
        ref = entry.reference.replace(entry.livre, frenchName);
    } else {
        ref = verseStart === verseEnd
            ? `${frenchName} ${entry.chapitre}:${verseStart}`
            : `${frenchName} ${entry.chapitre}:${verseStart}–${verseEnd}`;
    }

    if (entry.id) console.debug('[DailyVerses] Featured entry:', entry.id);

    const testamentBadge = entry.testament === 'Nouveau Testament'
        ? '<span class="testament-badge testament-nt">Nouveau Testament</span>'
        : '<span class="testament-badge testament-ot">Ancien Testament</span>';

    const dayNum = getDayOfYear();

    // Try to load in preferred language; fall back to Malagasy text from plan
    let versesHtml = '';
    try {
        const bookData = await loadBook(frenchName, lang);
        const chap = bookData[entry.chapitre];
        versesHtml = entry.versets.map(v => {
            const text = (chap && chap[v.numero]) ? chap[v.numero] : v.texte;
            return `<p class="daily-verse-line"><sup class="verse-sup">${v.numero}</sup>${escapeHtml(text)}</p>`;
        }).join('');
    } catch (e) {
        // Fallback: use Malagasy text from plan JSON
        versesHtml = entry.versets.map(v =>
            `<p class="daily-verse-line"><sup class="verse-sup">${v.numero}</sup>${escapeHtml(v.texte)}</p>`
        ).join('');
    }

    card.innerHTML = `
        <div class="featured-card-inner">
            <div class="featured-card-label">
                <span class="featured-day-badge">Jour ${dayNum}</span>
                ${testamentBadge}
            </div>
            <div class="featured-card-title">✨ Verset du Jour</div>
            <div class="daily-verse-ref featured-verse-ref">📖 ${escapeHtml(ref)}</div>
            <blockquote class="daily-verse-text featured-verse-text">${versesHtml}</blockquote>
            <div class="daily-verse-actions">
                <button class="btn btn-secondary daily-verse-chapter-btn">📖 Lire le chapitre en entier</button>
                <button class="btn btn-primary daily-verse-share-btn">✏️ Partager sur le fil</button>
            </div>
        </div>
    `;

    card.querySelector('.daily-verse-share-btn').addEventListener('click', () =>
        shareVerse({ bookName: frenchName, bookKey: frenchName, chapter: entry.chapitre, verseStart, verseEnd, verses: entry.versets, isNT: entry.testament === 'Nouveau Testament' })
    );
    card.querySelector('.daily-verse-chapter-btn').addEventListener('click', () =>
        openChapter(entry.livre, entry.chapitre)
    );

    return card;
}

function getVerseRange(versets) {
    const nums = versets.map(v => v.numero);
    return { verseStart: Math.min(...nums), verseEnd: Math.max(...nums) };
}

// ─────────────────────────────────────────
// Random Verses
// ─────────────────────────────────────────

async function pickVerseGroup(books, lang) {
    // 75% NT, 25% OT. In OT: 60% Psalms
    const isNT = Math.random() < 0.75;
    let book;
    if (isNT) {
        const ntPool = books.slice(NT_START_INDEX);
        book = ntPool[Math.floor(Math.random() * ntPool.length)];
    } else {
        const psalmsBook = books.find(b => b.name === 'Psaumes');
        const otherOT = books.slice(0, NT_START_INDEX).filter(b => b.name !== 'Psaumes');
        const usePsalms = psalmsBook && Math.random() < 0.6;
        book = usePsalms ? psalmsBook : otherOT[Math.floor(Math.random() * otherOT.length)];
    }

    const chapter = Math.floor(Math.random() * book.chapters) + 1;
    const bookData = await loadBook(book.key, lang);
    const chapterData = bookData[chapter];
    if (!chapterData) throw new Error(`Chapter ${chapter} not found`);

    const verseNums = Object.keys(chapterData).map(Number).sort((a, b) => a - b);
    if (verseNums.length === 0) throw new Error('Empty chapter');

    const groupSize = Math.floor(Math.random() * 5) + 1;
    const startIdx = Math.floor(Math.random() * verseNums.length);
    const selected = verseNums.slice(startIdx, startIdx + groupSize);
    const verses = selected.map(n => ({ numero: n, texte: chapterData[n] }));

    return {
        bookName: book.name,
        bookKey: book.key,
        chapter,
        verseStart: selected[0],
        verseEnd: selected[selected.length - 1],
        verses,
        isNT,
    };
}

function buildVerseCard(group) {
    const card = document.createElement('div');
    card.className = 'daily-verse-card';

    const ref = group.verseStart === group.verseEnd
        ? `${group.bookName} ${group.chapter}:${group.verseStart}`
        : `${group.bookName} ${group.chapter}:${group.verseStart}–${group.verseEnd}`;

    const testamentBadge = group.isNT
        ? '<span class="testament-badge testament-nt">Nouveau Testament</span>'
        : '<span class="testament-badge testament-ot">Ancien Testament</span>';

    const versesHtml = group.verses.map(v =>
        `<p class="daily-verse-line"><sup class="verse-sup">${v.numero}</sup>${escapeHtml(v.texte)}</p>`
    ).join('');

    card.innerHTML = `
        <div class="daily-verse-meta">${testamentBadge}</div>
        <div class="daily-verse-ref">📖 ${escapeHtml(ref)}</div>
        <blockquote class="daily-verse-text">${versesHtml}</blockquote>
        <div class="daily-verse-actions">
            <button class="btn btn-secondary daily-verse-chapter-btn">📖 Lire le chapitre en entier</button>
            <button class="btn btn-primary daily-verse-share-btn">✏️ Partager sur le fil</button>
        </div>
    `;

    card.querySelector('.daily-verse-share-btn').addEventListener('click', () => shareVerse(group));
    card.querySelector('.daily-verse-chapter-btn').addEventListener('click', () => openChapter(group.bookKey, group.chapter));
    return card;
}

// ─────────────────────────────────────────
// Open chapter on sidebyside-bible.com
// ─────────────────────────────────────────

function openChapter(bookKey, chapter) {
    const url = `https://sidebyside-bible.com/?book=${encodeURIComponent(bookKey)}&chapter=${chapter}`;
    window.open(url, '_blank', 'noopener,noreferrer');
}

// ─────────────────────────────────────────
// Share → pre-fill compose form
// ─────────────────────────────────────────

function shareVerse(group) {
    const feedTab = document.querySelector('.tab-btn[data-tab="feed"]');
    if (feedTab) feedTab.click();

    const bookInput = document.querySelector('#post-book');
    const chapterInput = document.querySelector('#post-chapter');
    const verseStartInput = document.querySelector('#post-verse-start');
    const verseEndInput = document.querySelector('#post-verse-end');
    const selectedVerseDisplay = document.querySelector('#selected-verse-display');

    if (bookInput) bookInput.value = group.bookKey;
    if (chapterInput) chapterInput.value = group.chapter;
    if (verseStartInput) verseStartInput.value = group.verseStart;
    if (verseEndInput) verseEndInput.value = group.verseEnd;

    if (selectedVerseDisplay) {
        const ref = group.verseStart === group.verseEnd
            ? `${group.bookName} ${group.chapter}:${group.verseStart}`
            : `${group.bookName} ${group.chapter}:${group.verseStart}-${group.verseEnd}`;

        const verseText = group.verses.map(v =>
            `<span class="verse-num">${v.numero}</span> ${escapeHtml(v.texte)}`
        ).join(' ');

        selectedVerseDisplay.innerHTML = `
            <div class="verse-link-display">
                <span class="verse-info">📖 ${escapeHtml(ref)}</span>
                <button type="button" class="remove-verse-btn">×</button>
            </div>
            <div class="verse-text-preview">${verseText}</div>
        `;
        selectedVerseDisplay.style.display = 'block';

        selectedVerseDisplay.querySelector('.remove-verse-btn').addEventListener('click', () => {
            if (bookInput) bookInput.value = '';
            if (chapterInput) chapterInput.value = '';
            if (verseStartInput) verseStartInput.value = '';
            if (verseEndInput) verseEndInput.value = '';
            selectedVerseDisplay.style.display = 'none';
        });
    }

    const composeSection = document.querySelector('.compose-section');
    if (composeSection) {
        setTimeout(() => {
            composeSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            const textarea = document.querySelector('#post-text');
            if (textarea) setTimeout(() => textarea.focus(), 300);
        }, 100);
    }
}

// ─────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────

function clearRefreshTimer() {
    if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = null; }
}

function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
}
